/* global fetch */

import { config } from '../config';
import { headers, APIResponse, stleaf, fetchAPI, SpanTag } from '../helpers';

export type AiModel = 'basic' | 'asphalt-patch' | 'stockpile';

export interface AiDetectMediaTextRequest {
  prompt: string;
  mediaId: string;
  layerId?: string;
  confidence: number;
  resolution: 'low' | 'medium' | 'high';
  colour?: string;
  name?: string;
  method?: string;
  model?: AiModel;
  removeObjects?: boolean;
}

export interface AiDetectMediaTextBulkRequest {
  prompt: string;
  mediaIds: string[];
  layerIds?: string[];
  confidence: number;
  resolution: 'low' | 'medium' | 'high';
  colour?: string;
  name?: string;
  method?: string;
  model?: AiModel;
}

export interface AiDetectMediaClickRequest {
  points: Array<[number, number]>;
  mediaId: string;
  layerId?: string;
  colour?: string;
  name?: string;
  method?: string;
  removeObjects?: boolean;
}

export interface AiDetectOrthomosaicRequest {
  layerId: string;
  prompt: string;
  folderId: string;
  mapId: string;
  windowSize: number;
  windowOverlap: number;
  confidence?: number;
  outputType?: 'polygon' | 'point' | 'volumetric';
  model?: AiModel;
  /** The survey's DSM: required by the stockpile model (it uses elevation). */
  dsmLayerId?: string;
}

export interface AiGenerateWindowSizeRequest {
  prompt: string;
}

export interface AiGenerateWindowSizeResponse {
  windowSize: number;
}

export interface AiDetectScreenshotRequest {
  imageBase64: string;
  prompt: string;
  confidence: number;
  outputType: 'polygon' | 'point';
  mapId: string;
  folderId: string;
  bounds: {
    north: number;
    south: number;
    east: number;
    west: number;
  };
  center: {
    longitude: number;
    latitude: number;
  };
  zoom: number;
  model?: AiModel;
}

export interface AiDetectScreenshotResponse {
  success: boolean;
  polygonCount: number;
  bounds: {
    north: number;
    south: number;
    east: number;
    west: number;
  };
  outputType: 'polygon' | 'point';
}

export interface AiDetectResponse {
  success: boolean;
  results?: any[];
  error?: string;
  polygons?: number[][][];
  objects_removed_count?: number;
}

export const aiDetectMediaText = (
  st: SpanTag,
  request: AiDetectMediaTextRequest,
): Promise<APIResponse<AiDetectResponse>> => {
  const url = `${config.API_URL}/v1/ai/detect/media-text`;
  return fetchAPI(stleaf(st, 'ai8det'), url, {
    method: 'POST',
    credentials: 'include',
    headers,
    body: JSON.stringify(request),
  });
};

export const aiDetectMediaTextBulk = (
  st: SpanTag,
  request: AiDetectMediaTextBulkRequest,
): Promise<APIResponse<AiDetectResponse>> => {
  const url = `${config.API_URL}/v1/ai/detect/media-text-bulk`;
  return fetchAPI(stleaf(st, 'ai8blk'), url, {
    method: 'POST',
    credentials: 'include',
    headers,
    body: JSON.stringify(request),
  });
};

export const aiDetectMediaClick = (
  st: SpanTag,
  request: AiDetectMediaClickRequest,
): Promise<APIResponse<AiDetectResponse>> => {
  const url = `${config.API_URL}/v1/ai/detect/media-click`;
  return fetchAPI(stleaf(st, 'ai8clk'), url, {
    method: 'POST',
    credentials: 'include',
    headers,
    body: JSON.stringify(request),
  });
};

export const aiDetectOrthomosaic = (
  st: SpanTag,
  request: AiDetectOrthomosaicRequest,
): Promise<APIResponse<AiDetectResponse>> => {
  const url = `${config.API_URL}/v1/ai/detect/orthomosaic`;
  return fetchAPI(stleaf(st, 'ai8ort'), url, {
    method: 'POST',
    credentials: 'include',
    headers,
    body: JSON.stringify(request),
  });
};

export const aiDetectScreenshot = (
  st: SpanTag,
  request: AiDetectScreenshotRequest,
): Promise<APIResponse<AiDetectScreenshotResponse>> => {
  const url = `${config.API_URL}/v1/ai/detect/screenshot`;
  return fetchAPI(stleaf(st, 'ai8scr'), url, {
    method: 'POST',
    credentials: 'include',
    headers,
    body: JSON.stringify(request),
  });
};

export const aiGenerateWindowSize = (
  st: SpanTag,
  request: AiGenerateWindowSizeRequest,
): Promise<APIResponse<AiGenerateWindowSizeResponse>> => {
  const url = `${config.API_URL}/v1/ai/window-size`;
  return fetchAPI(stleaf(st, 'ai8gen'), url, {
    method: 'POST',
    credentials: 'include',
    headers,
    body: JSON.stringify(request),
  });
};

export interface AiDetection {
  id: string;
  prompt: string | null;
  numOutputs: string | null;
  method: string | null;
  status: string | null;
  createdAt: string;
  mapId: string | null;
  mapName: string | null;
  mediaId: string | null;
  layerId: string | null;
  mediaS3Key: string | null;
  mediaThumbUrl: string | null;
  thumbnailUrl: string | null;
  creatorFname: string | null;
  creatorLname: string | null;
  detectionCenter: {
    longitude: number;
    latitude: number;
  } | null;
  detectionZoom: number | null;
}

export const getOrganisationDetections = (
  st: SpanTag,
  workspaceId: string,
  opts?: { limit?: number },
): Promise<APIResponse<AiDetection[]>> => {
  const url = new URL(`${config.API_URL}/v1/ai/detections/${workspaceId}`);
  if (opts?.limit) {
    url.searchParams.set('limit', opts.limit.toString());
  }
  return fetchAPI(stleaf(st, 'aidetl'), url.toString(), {
    method: 'GET',
    credentials: 'include',
    headers,
  });
};
